/**
 * GraphQL queries for scheduled appointments with pagination and search
 */

// Helper to build dynamic where clause
export const buildAppointmentWhereClause = (
  appointmentType: number,
  userId?: number,
  isAdmin: boolean = false,
  filters?: {
    patientName?: string;
    appointmentDate?: string;
    clinicName?: string;
    userName?: string;
  },
  clinicIds?: number[]
): string => {
  const conditions: string[] = [`appointment_type: {_eq: ${appointmentType}}`];
  
  // Add user filter for non-admins
  if (!isAdmin && userId) {
    conditions.push(`user_id: {_eq: ${userId}}`);
  }

  // Add clinic_id filter when clinicIds are provided
  if (clinicIds && clinicIds.length > 0) {
    conditions.push(`clinic_id: {_in: [${clinicIds.join(', ')}]}`);
  }
  
  // Add search filters
  if (filters?.patientName) {
    conditions.push(`patient_name: {_ilike: "%${filters.patientName}%"}`);
  }
  
  if (filters?.appointmentDate) {
    // Filter by date - appointments that start on the given date
    conditions.push(`appointment_date_start_time: {_gte: "${filters.appointmentDate}T00:00:00", _lt: "${filters.appointmentDate}T23:59:59"}`);
  }
  
  if (filters?.clinicName) {
    conditions.push(`clinic_name: {_ilike: "%${filters.clinicName}%"}`);
  }
  
  if (filters?.userName) {
    // For virtual appointments, search user_name; for in-person, search facility_name
    if (appointmentType === 1) {
      conditions.push(`user_name: {_ilike: "%${filters.userName}%"}`);
    } else {
      conditions.push(`facility_name: {_ilike: "%${filters.userName}%"}`);
    }
  }
  
  return `{${conditions.join(', ')}}`;
};

// Dynamic query builder for appointments with filters
export const buildScheduledAppointmentsQuery = (
  appointmentType: number,
  userId?: number,
  isAdmin: boolean = false,
  filters?: {
    patientName?: string;
    appointmentDate?: string;
    clinicName?: string;
    userName?: string;
  },
  clinicIds?: number[]
): string => {
  const whereClause = buildAppointmentWhereClause(appointmentType, userId, isAdmin, filters, clinicIds);
  const queryName = appointmentType === 1 
    ? (isAdmin ? 'getScheduledVirtualAppointmentsAllFiltered' : 'getScheduledVirtualAppointmentsFiltered')
    : (isAdmin ? 'getScheduledInPersonAppointmentsAllFiltered' : 'getScheduledInPersonAppointmentsFiltered');
  
  return `
    query ${queryName}($limit: Int!, $offset: Int!) {
      vw_patient_appointment_details(
        where: ${whereClause}
        limit: $limit
        offset: $offset
        order_by: {appointment_date_start_time: asc}
      ) {
        appointment_date_end_time
        appointment_date_start_time
        appointment_id
        appointment_type
        clinic_facility_id
        clinic_id
        patient_name
        user_id
        enrollment_patient_id
        clinic_name
        clinic_timezone
        user_timezone
        facility_name
        user_name
      }
      vw_patient_appointment_details_aggregate(
        where: ${whereClause}
      ) {
        aggregate {
          count
        }
      }
    }
  `;
};

// Original static queries (kept for backward compatibility)
export const GET_SCHEDULED_VIRTUAL_APPOINTMENTS = `
  query getScheduledVirtualAppointments($userId: Int, $limit: Int!, $offset: Int!) {
    vw_patient_appointment_details(
      where: {appointment_type: {_eq: 1}, user_id: {_eq: $userId}}
      limit: $limit
      offset: $offset
      order_by: {appointment_date_start_time: asc}
    ) {
      appointment_date_end_time
      appointment_date_start_time
      appointment_id
      appointment_type
      clinic_facility_id
      clinic_id
      patient_name
      user_id
      enrollment_patient_id
      clinic_name
      clinic_timezone
      user_timezone
      facility_name
      user_name
    }
    vw_patient_appointment_details_aggregate(
      where: {appointment_type: {_eq: 1}, user_id: {_eq: $userId}}
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GET_SCHEDULED_VIRTUAL_APPOINTMENTS_ALL = `
  query getScheduledVirtualAppointmentsAll($limit: Int!, $offset: Int!) {
    vw_patient_appointment_details(
      where: {appointment_type: {_eq: 1}}
      limit: $limit
      offset: $offset
      order_by: {appointment_date_start_time: asc}
    ) {
      appointment_date_end_time
      appointment_date_start_time
      appointment_id
      appointment_type
      clinic_facility_id
      clinic_id
      patient_name
      user_id
      enrollment_patient_id
      clinic_name
      clinic_timezone
      user_timezone
      facility_name
      user_name
    }
    vw_patient_appointment_details_aggregate(
      where: {appointment_type: {_eq: 1}}
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GET_SCHEDULED_IN_PERSON_APPOINTMENTS = `
  query getScheduledInPersonAppointments($userId: Int, $limit: Int!, $offset: Int!) {
    vw_patient_appointment_details(
      where: {appointment_type: {_eq: 2}, user_id: {_eq: $userId}}
      limit: $limit
      offset: $offset
      order_by: {appointment_date_start_time: asc}
    ) {
      appointment_date_end_time
      appointment_date_start_time
      appointment_id
      appointment_type
      clinic_facility_id
      clinic_id
      patient_name
      user_id
      enrollment_patient_id
      clinic_name
      clinic_timezone
      user_timezone
      facility_name
      user_name
    }
    vw_patient_appointment_details_aggregate(
      where: {appointment_type: {_eq: 2}, user_id: {_eq: $userId}}
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GET_SCHEDULED_IN_PERSON_APPOINTMENTS_ALL = `
  query getScheduledInPersonAppointmentsAll($limit: Int!, $offset: Int!) {
    vw_patient_appointment_details(
      where: {appointment_type: {_eq: 2}}
      limit: $limit
      offset: $offset
      order_by: {appointment_date_start_time: asc}
    ) {
      appointment_date_end_time
      appointment_date_start_time
      appointment_id
      appointment_type
      clinic_facility_id
      clinic_id
      patient_name
      user_id
      enrollment_patient_id
      clinic_name
      clinic_timezone
      user_timezone
      facility_name
      user_name
    }
    vw_patient_appointment_details_aggregate(
      where: {appointment_type: {_eq: 2}}
    ) {
      aggregate {
        count
      }
    }
  }
`;
